// Pure presenter for the Argos-style listing-activity overlay on /listing
// pages. Decides which social-proof messages show and their wording; the
// component only rotates through whatever this returns. Kept out of the
// component so the floors and gating are unit tested without rendering.

import { favouriteCountLabel, FAVOURITE_COUNT_MIN_DISPLAY } from "./favouriteCount";

// GET /whitelabel/property-activity/:propertyId response data (count-only,
// PII-free; `popular` is decided server-side against named thresholds). View
// windows are nested and rolling (today ⊆ this week ⊆ this month). The week and
// month fields are optional — an API from before they shipped simply omits
// them, and the presenter treats a missing window as zero.
export interface PropertyActivity {
  viewsToday: number;
  viewsThisWeek?: number;
  viewsThisMonth?: number;
  viewsLastHour: number;
  favouritedByUsers: number;
  popular: boolean;
}

// Per-window floors: the server's `popular` gate admits the property (e.g. on a
// busy month alone), but an individual window below its floor reads as weak
// social proof and is dropped rather than shown.
export const ACTIVITY_MIN_VIEWS_LAST_HOUR = 2;
export const ACTIVITY_MIN_VIEWS_TODAY = 5;
export const ACTIVITY_MIN_VIEWS_WEEK = 10;
export const ACTIVITY_MIN_VIEWS_MONTH = 20;

// How long each rotating message stays on screen (drives the component's
// fade-in/out cycle).
export const ACTIVITY_MESSAGE_DURATION_MS = 6000;

function qualifies(count: number, floor: number): boolean {
  return Number.isInteger(count) && count >= floor;
}

// The view windows in display priority — tightest (most immediate) first — with
// both the rotating-overlay wording and the compact grid-card wording. Single
// source so `listingActivityMessages` and `listingActivityBadge` cannot drift.
// Missing (optional) week/month counts coerce to 0.
function viewWindows(activity: PropertyActivity) {
  const week = activity.viewsThisWeek ?? 0;
  const month = activity.viewsThisMonth ?? 0;
  return [
    { count: activity.viewsLastHour, floor: ACTIVITY_MIN_VIEWS_LAST_HOUR, message: `Viewed ${activity.viewsLastHour} times in the last hour`, badge: `${activity.viewsLastHour} viewed this hour` },
    { count: activity.viewsToday, floor: ACTIVITY_MIN_VIEWS_TODAY, message: `Viewed ${activity.viewsToday} times today`, badge: `${activity.viewsToday} viewed today` },
    { count: week, floor: ACTIVITY_MIN_VIEWS_WEEK, message: `Viewed ${week} times this week`, badge: `${week} viewed this week` },
    { count: month, floor: ACTIVITY_MIN_VIEWS_MONTH, message: `Viewed ${month} times this month`, badge: `${month} viewed this month` },
  ];
}

// Overlay messages: the SINGLE tightest qualifying view window (the windows are
// nested, so showing all four would just escalate the same count) plus the
// favourites line. Empty when the overlay must not show: not popular, failed
// load, or every signal below its floor.
export function listingActivityMessages(
  activity: PropertyActivity | null | undefined
): string[] {
  if (!activity || activity.popular !== true) return [];

  const messages: string[] = [];
  const topWindow = viewWindows(activity).find((w) => qualifies(w.count, w.floor));
  if (topWindow) messages.push(topWindow.message);
  const favourites = favouriteCountLabel(activity.favouritedByUsers);
  if (favourites) messages.push(favourites);
  return messages;
}

// Compact single-signal badge for a catalogue GRID card (no room to rotate).
// Same popular gate, floors and priority (last-hour -> today -> this week ->
// this month -> favourites) as the overlay, but returns only the strongest
// qualifying signal with short card wording, plus its `kind` so the card can
// pick an icon. Null when not popular or every signal is below floor.
export interface ListingActivityBadge {
  text: string;
  kind: "views" | "favourites";
}

export function listingActivityBadge(
  activity: PropertyActivity | null | undefined
): ListingActivityBadge | null {
  if (!activity || activity.popular !== true) return null;

  const topWindow = viewWindows(activity).find((w) => qualifies(w.count, w.floor));
  if (topWindow) return { text: topWindow.badge, kind: "views" };
  if (qualifies(activity.favouritedByUsers, FAVOURITE_COUNT_MIN_DISPLAY)) {
    return { text: `Favourited by ${activity.favouritedByUsers}`, kind: "favourites" };
  }
  return null;
}
